import { create } from 'zustand';
import { apiRequest } from '@/lib/queryClient';
import { toast } from '@/hooks/use-toast';
import { multiplyMoney, sumMoney, parsePrice, formatPriceForDb } from '@shared/currency';
import { getDisplayBrand, isFactoryRefurbished } from '@shared/palletDisplay';
import { isAggregateGrade } from '@shared/grading';
import { nanoid } from 'nanoid';

// Helper to handle session expiry gracefully - redirects to login with message
function handleSessionExpiredForCart(action: string) {
  // Store current location for redirect after login
  const currentPath = window.location.pathname + window.location.search;
  if (currentPath && currentPath !== '/login' && currentPath !== '/') {
    sessionStorage.setItem('auth_redirect', currentPath);
  }
  
  // Show friendly toast explaining what happened
  toast({
    title: "Please sign in",
    description: `Your session has expired. Please sign in to ${action}.`,
    variant: "default",
  });
  
  // Short delay to let toast show before redirecting
  setTimeout(() => {
    window.location.href = '/login';
  }, 500);
}

export interface CartItem {
  id: string;
  modelId: string;
  inventoryId?: string;
  unitId?: string;
  brand: string;
  modelName: string;
  description?: string;
  msrp?: string;
  grade: 'A' | 'B+' | 'B-';
  quantity: number;
  price: number; // Decimal price (e.g., 650.00)
  serialNumber?: string;
  videoUrl?: string;
  dimensions: {
    width: number;
    depth: number;
  };
  imageUrl?: string;
  maxQuantity?: number;
  warehouseCode?: string;
  city?: string;
  region?: string;
  minOrderQty?: number;
  orderMultiple?: number;
  /** ISO date string for pre-arrival / presale items (e.g. "2025-05-12") */
  expectedShipDate?: string | null;
  /** True when the cart line is a Virtual AC Pallet (model.isPallet=true).
   *  Used presentation-side to render "Factory Refurbished" instead of the
   *  generic open-box B grade pill. The DB grade column stays "B" for legacy
   *  reasons; this flag is purely a display hint persisted with the cart. */
  isPallet?: boolean;
  /** True when the cart line is part of a factory-refurbished pallet program
   *  (currently only AC pallets — see shared/palletDisplay.isFactoryRefurbished).
   *  Cart drawer + checkout consume THIS flag (not raw isPallet) to decide
   *  whether to show the green Factory Refurbished pill in place of the open-box
   *  GradeBadge, so future non-refurbished pallet programs (e.g. microwave
   *  bundles) won't accidentally inherit the FR pill. */
  isFactoryRefurbished?: boolean;
  /** Optional model.categoryId snapshot — drives per-category grading
   *  copy + the pallet inventory pill in the cart drawer & checkout
   *  (Task #156). Backward-compatible: legacy lines without this still
   *  render the default refrigerator pill. */
  categoryId?: string | null;
}

// Snap a quantity to the nearest valid multiple, enforcing minOrderQty.
// When rounding-up to the next multiple would exceed maxQty, cap at maxQty
// so customers can still order the remaining stock (remainder-ordering rule).
function snapQty(quantity: number, minOrderQty: number = 1, orderMultiple: number = 1, maxQty?: number): number {
  if (quantity <= 0) return 0;
  let snapped = quantity;
  if (orderMultiple > 1) {
    snapped = Math.ceil(quantity / orderMultiple) * orderMultiple;
    // Ceiling exceeds available stock → allow the remainder instead
    if (maxQty !== undefined && snapped > maxQty) {
      snapped = maxQty;
    }
  }
  snapped = Math.max(snapped, minOrderQty);
  if (maxQty !== undefined) snapped = Math.min(snapped, maxQty);
  return snapped;
}

// Guest cart localStorage helpers
const GUEST_CART_KEY = 'guest-cart';
const GUEST_PO_KEY = 'guest-po';
const CART_SCHEMA_VERSION = 2; // Version 1: cents, Version 2: decimals

interface StoredGuestCart {
  version?: number;
  items: CartItem[];
}

function loadGuestCart(): CartItem[] {
  try {
    const stored = localStorage.getItem(GUEST_CART_KEY);
    if (!stored) return [];
    
    const parsed = JSON.parse(stored);
    
    // Detect format:
    // 1. Legacy: plain array (version 1, cent-based)
    // 2. Versioned: object with version field
    // 3. Corrupted: anything else
    
    let items: CartItem[];
    let needsMigration = false;
    
    if (Array.isArray(parsed)) {
      // Legacy array format → needs migration from cents to decimals
      items = parsed;
      needsMigration = true;
    } else if (parsed && typeof parsed === 'object' && 'version' in parsed && 'items' in parsed) {
      // Versioned format → check version number
      const data = parsed as StoredGuestCart;
      items = data.items || [];
      needsMigration = (data.version === 1); // Only version 1 needs migration
    } else {
      // Corrupted or unrecognized format
      console.warn('[Cart] Unrecognized localStorage format, clearing');
      return [];
    }
    
    // Apply migration if needed (convert cents to decimals)
    if (needsMigration) {
      return items.map(item => {
        // Robust sanitizer for legacy price formats
        // Handles: "65000", "65,000", " 65000 ", "$65,000", etc.
        const priceStr = String(item.price);
        
        // Remove currency symbols and whitespace first
        let cleaned = priceStr.replace(/[$€£¥₹\s]/g, '');
        
        // Detect and normalize separators based on which separator appears LAST
        // US format: "65,000.50" → last separator is period (decimal)
        // EU format: "65.000,00" → last separator is comma (decimal)
        const lastComma = cleaned.lastIndexOf(',');
        const lastPeriod = cleaned.lastIndexOf('.');
        
        if (lastComma > lastPeriod) {
          // Comma is rightmost → European format (comma = decimal, period = thousands)
          // "65.000,00" → remove periods, convert comma to period
          cleaned = cleaned.replace(/\./g, '').replace(',', '.');
        } else if (lastPeriod > lastComma) {
          // Period is rightmost → US format (period = decimal, comma = thousands)
          // "65,000.50" → remove commas
          cleaned = cleaned.replace(/,/g, '');
        } else if (lastComma >= 0) {
          // Only has comma(s), no period
          // If last comma is in decimal position (within last 3 chars), treat as decimal
          if (cleaned.length - lastComma <= 3 && cleaned.length - lastComma > 1) {
            // "65000,50" → "65000.50"
            cleaned = cleaned.replace(',', '.');
          } else {
            // "65,000" → "65000"
            cleaned = cleaned.replace(/,/g, '');
          }
        }
        
        // Try to parse as number
        const parsedCents = parseFloat(cleaned);
        
        // If parsing failed completely, default to 0 and log warning
        if (!Number.isFinite(parsedCents)) {
          console.warn(`[Cart] Migration: Could not parse legacy price "${item.price}" (cleaned: "${cleaned}"), defaulting to $0.00`);
          return {
            ...item,
            price: 0
          };
        }
        
        // Successfully parsed - apply cent-to-dollar conversion
        return {
          ...item,
          // Divide by 100 to convert cents to dollars (50 cents → $0.50, 65000 cents → $650.00)
          price: parsePrice(parsedCents / 100)
        };
      });
    }
    
    // No migration needed → prices are already decimals
    return items.map(item => ({
      ...item,
      // Just ensure price is properly parsed
      price: parsePrice(item.price)
    }));
  } catch (error) {
    console.error('[Cart] Failed to load guest cart:', error);
    return [];
  }
}

function saveGuestCart(items: CartItem[]): void {
  try {
    const data: StoredGuestCart = {
      version: CART_SCHEMA_VERSION,
      items
    };
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save guest cart to localStorage:', error);
  }
}

function clearGuestCart(): void {
  try {
    localStorage.removeItem(GUEST_CART_KEY);
    localStorage.removeItem(GUEST_PO_KEY);
  } catch (error) {
    console.error('Failed to clear guest cart from localStorage:', error);
  }
}

function loadGuestPoNumber(): string | null {
  try {
    return localStorage.getItem(GUEST_PO_KEY);
  } catch {
    return null;
  }
}

function saveGuestPoNumber(poNumber: string | null): void {
  try {
    if (poNumber) {
      localStorage.setItem(GUEST_PO_KEY, poNumber);
    } else {
      localStorage.removeItem(GUEST_PO_KEY);
    }
  } catch (error) {
    console.error('Failed to save guest PO number to localStorage:', error);
  }
}

function transformServerCartItems(rawItems: any[]): CartItem[] {
  return rawItems.map((item: any) => ({
    id: item.id,
    modelId: item.modelId,
    inventoryId: item.inventoryId,
    unitId: item.unitId,
    brand: item.model ? getDisplayBrand(item.model) : '',
    modelName: item.model?.mfgSku || '',
    description: item.model?.description || '',
    msrp: item.model?.msrp || '',
    grade: item.grade as 'A' | 'B+' | 'B-',
    quantity: item.quantity,
    price: parsePrice(item.price),
    serialNumber: item.unit?.serialNumber,
    videoUrl: item.unit?.videoUrl || undefined,
    dimensions: {
      width: item.model?.dimensionsWidth || 0,
      depth: item.model?.dimensionsDepth || 0,
    },
    imageUrl: item.model?.imageUrl || undefined,
    maxQuantity: item.maxQuantity,
    warehouseCode: item.warehouseCode || undefined,
    city: item.city || undefined,
    region: item.region || undefined,
    minOrderQty: item.model?.minOrderQty ?? 1,
    orderMultiple: item.model?.orderMultiple ?? 1,
    expectedShipDate: item.expectedShipDate ?? null,
    isPallet: item.model?.isPallet ?? undefined,
    isFactoryRefurbished: item.model ? isFactoryRefurbished(item.model) : undefined,
  }));
}

export interface CartonizationResult {
  totalAreaUsed: number;
  capacityUsedPercentage: number;
  remainingArea: number;
  canFit: boolean;
  warningLevel: 'low' | 'medium' | 'high';
}

interface CartState {
  items: CartItem[];
  poNumber: string | null;
  lastAddedGroupKey: string | null;
  highlightedModelId: string | null;
  isLoaded: boolean;
  currentUserId: string | null;
  cartMode: 'guest' | 'authenticated';
  isGuestMode: () => boolean;
  loadCart: () => Promise<void>;
  resetCart: () => void;
  addItem: (item: CartItem) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  updatePoNumber: (poNumber: string) => Promise<void>;
  clearCart: () => Promise<void>;
  getTotalAmount: () => number;
  getItemCount: () => number;
  calculateCapacity: (truckSize: string) => CartonizationResult;
  setHighlightedModel: (modelId: string | null) => void;
  getWarehouseCodes: () => string[];
  migrateGuestCart: () => Promise<void>;
}

export const useCartStore = create<CartState>((set, get) => ({
  items: [],
  poNumber: null,
  lastAddedGroupKey: null,
  highlightedModelId: null,
  isLoaded: false,
  currentUserId: null,
  cartMode: 'guest',

  isGuestMode: () => get().cartMode === 'guest',

  resetCart: () => {
    const wasAuthenticated = get().currentUserId !== null;
    
    set({ 
      items: [], 
      poNumber: null,
      isLoaded: false, 
      currentUserId: null, 
      lastAddedGroupKey: null,
      highlightedModelId: null,
      cartMode: 'guest'
    });
    
    // Only rehydrate guest cart if user was NOT authenticated (prevent cross-account leakage)
    if (!wasAuthenticated) {
      const guestItems = loadGuestCart();
      const guestPo = loadGuestPoNumber();
      set({ items: guestItems, poNumber: guestPo, isLoaded: true });
      console.log('[Cart] Rehydrated guest cart after reset:', guestItems.length, 'items');
    } else {
      set({ isLoaded: true });
      console.log('[Cart] Logged out authenticated user, cleared cart without rehydration');
    }
  },

  loadCart: async () => {
    try {
      const response = await fetch('/api/cart', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          // User not authenticated - load guest cart from localStorage
          const guestItems = loadGuestCart();
          const guestPo = loadGuestPoNumber();
          set({ items: guestItems, poNumber: guestPo, isLoaded: true, currentUserId: null, cartMode: 'guest' });
          console.log('[Cart] Loaded guest cart:', guestItems.length, 'items');
          return;
        }
        throw new Error('Failed to load cart');
      }

      const cart = await response.json();
      
      // Check if any items were auto-removed due to unavailability (e.g., strict_location items without warehouse locations)
      if (cart.cartUpdates?.removed?.length > 0) {
        const removedCount = cart.cartUpdates.removed.length;
        const firstRemoved = cart.cartUpdates.removed[0];
        toast({
          title: `${removedCount} item${removedCount > 1 ? 's' : ''} removed from cart`,
          description: removedCount === 1 
            ? `${firstRemoved.modelName}: ${firstRemoved.reason}`
            : `Some items are no longer available for purchase`,
          variant: "destructive",
        });
      }
      
      if (cart.cartUpdates?.adjusted?.length > 0) {
        const adjustedCount = cart.cartUpdates.adjusted.length;
        const firstAdjusted = cart.cartUpdates.adjusted[0];
        toast({
          title: `${adjustedCount} item${adjustedCount > 1 ? 's' : ''} quantity adjusted`,
          description: adjustedCount === 1 
            ? `${firstAdjusted.modelName}: ${firstAdjusted.reason}`
            : `Some items had their quantities reduced due to limited stock`,
          variant: "default",
        });
      }
      
      // Transform backend cart items to match our CartItem interface
      const items: CartItem[] = transformServerCartItems(cart.items);

      set({ items, poNumber: cart.poNumber || null, isLoaded: true, currentUserId: cart.userId, cartMode: 'authenticated' });
      
      // Migrate guest cart if it exists
      await get().migrateGuestCart();
    } catch (error) {
      console.error('Failed to load cart:', error);
      set({ isLoaded: true, currentUserId: null, cartMode: 'guest' });
    }
  },

  addItem: async (item: CartItem) => {
    const groupKey = `${item.modelName}-${item.grade}`;

    if (get().isGuestMode()) {
      // Guest mode: Generate deterministic ID and save to localStorage
      const guestId = `guest-${item.modelId}-${item.grade}-${item.unitId ?? nanoid(6)}`;
      // Apply ordering rules to the initial quantity
      const snappedQty = snapQty(item.quantity, item.minOrderQty, item.orderMultiple, item.maxQuantity);
      const guestItem: CartItem = {
        ...item,
        id: guestId,
        quantity: snappedQty > 0 ? snappedQty : item.quantity,
        inventoryId: item.inventoryId,  // Preserve inventoryId
        unitId: item.unitId,            // Preserve unitId
      };

      // Optimistic update with guest cart logic
      set((state) => {
        const existingItemIndex = state.items.findIndex(
          (existing) => 
            existing.modelId === item.modelId && 
            existing.grade === item.grade &&
            (isAggregateGrade(item.grade) || existing.unitId === item.unitId)
        );
        
        let updatedItems: CartItem[];
        if (existingItemIndex >= 0 && isAggregateGrade(item.grade)) {
          // Aggregate A-grade quantities with ordering-rule enforcement
          updatedItems = [...state.items];
          const existing = updatedItems[existingItemIndex];
          const combined = snapQty(existing.quantity + item.quantity, existing.minOrderQty, existing.orderMultiple, existing.maxQuantity);
          updatedItems[existingItemIndex] = { ...existing, quantity: combined > 0 ? combined : existing.quantity + item.quantity };
        } else if (existingItemIndex >= 0) {
          // Don't add duplicate B+/B- units (enforce uniqueness by unitId)
          return state;
        } else {
          // Add new item
          updatedItems = [...state.items, guestItem];
        }
        
        // Save to localStorage
        saveGuestCart(updatedItems);
        return { items: updatedItems, lastAddedGroupKey: groupKey };
      });
      return;
    }

    // Authenticated mode: Optimistic update
    set((state) => {
      const existingItemIndex = state.items.findIndex(
        (existing) => 
          existing.modelId === item.modelId && 
          existing.grade === item.grade &&
          (isAggregateGrade(item.grade) || existing.unitId === item.unitId)
      );
      
      if (existingItemIndex >= 0 && isAggregateGrade(item.grade)) {
        // Update quantity for A-grade items
        const updatedItems = [...state.items];
        updatedItems[existingItemIndex].quantity += item.quantity;
        return { items: updatedItems, lastAddedGroupKey: groupKey };
      } else if (existingItemIndex >= 0) {
        // Don't add duplicate B+/B- units
        return state;
      } else {
        // Add new item
        return { items: [...state.items, item], lastAddedGroupKey: groupKey };
      }
    });

    // Sync with backend
    try {
      const response = await apiRequest('POST', '/api/cart/items', {
        modelId: item.modelId,
        inventoryId: item.inventoryId,
        unitId: item.unitId,
        grade: item.grade,
        quantity: item.quantity,
        price: formatPriceForDb(item.price), // Format decimal price for database
      });
      
      // Successfully added to backend - now reload cart to get accurate state
      try {
        await get().loadCart();
      } catch (loadError) {
        console.error('Failed to reload cart after adding item:', loadError);
        // Cart reload failed, but item was added successfully to backend
        // Keep the optimistic update rather than reverting
      }
    } catch (error: any) {
      // Log with useful context - error.message for actual error info
      const errorMessage = error?.message || 'Unknown error';
      console.warn('[Cart] Failed to add item:', item.modelName, item.grade, '-', errorMessage);
      
      // Check if this is a session expiry error (401)
      if (errorMessage.includes('401') || errorMessage.toLowerCase().includes('unauthorized') || errorMessage.toLowerCase().includes('session')) {
        // Session expired - redirect to login gracefully
        handleSessionExpiredForCart('add items to your cart');
        return;
      }
      
      // Only revert on backend error (not cart reload error)
      // Show user-friendly error message
      if (errorMessage && errorMessage !== 'Unknown error') {
        toast({
          title: "Could not add to cart",
          description: errorMessage,
          variant: "destructive",
        });
      }
      
      // Revert optimistic update on backend error
      await get().loadCart();
    }
  },

  removeItem: async (itemId: string) => {
    if (get().isGuestMode()) {
      // Guest mode: Filter items and save to localStorage
      const updatedItems = get().items.filter(item => item.id !== itemId);
      set({ items: updatedItems });
      saveGuestCart(updatedItems);
      return;
    }

    // Authenticated mode: Optimistic update
    const previousItems = get().items;
    set((state) => ({
      items: state.items.filter(item => item.id !== itemId)
    }));

    // Sync with backend
    try {
      await apiRequest('DELETE', `/api/cart/items/${itemId}`, {});
    } catch (error: any) {
      console.error('Failed to remove item from cart:', error);
      
      // Check if this is a session expiry error (401)
      const errorMessage = error.message || '';
      if (errorMessage.includes('401') || errorMessage.toLowerCase().includes('unauthorized') || errorMessage.toLowerCase().includes('session')) {
        handleSessionExpiredForCart('manage your cart');
        return;
      }
      
      // Revert on error
      set({ items: previousItems });
      toast({
        title: "Failed to remove item",
        description: "Please try again",
        variant: "destructive",
      });
    }
  },

  updateQuantity: async (itemId: string, quantity: number) => {
    // Check inventory limits before updating
    const items = get().items;
    const item = items.find(i => i.id === itemId);
    
    if (item && item.maxQuantity !== undefined && quantity > item.maxQuantity) {
      toast({
        title: "Insufficient Inventory",
        description: `Only ${item.maxQuantity} unit(s) available for ${item.brand} ${item.modelName} (Grade ${item.grade})`,
        variant: "destructive",
      });
      return;
    }

    if (get().isGuestMode()) {
      // Guest mode: Update quantity with ordering-rule enforcement and save to localStorage
      const updatedItems = get().items.map(cartItem => {
        if (cartItem.id !== itemId) return cartItem;
        const snapped = snapQty(quantity, cartItem.minOrderQty, cartItem.orderMultiple, cartItem.maxQuantity);
        return { ...cartItem, quantity: snapped };
      }).filter(cartItem => cartItem.quantity > 0);
      
      set({ items: updatedItems });
      saveGuestCart(updatedItems);
      return;
    }

    // Authenticated mode: Optimistic update
    const previousItems = get().items;
    set((state) => ({
      items: state.items.map(item => 
        item.id === itemId 
          ? { ...item, quantity: Math.max(0, quantity) }
          : item
      ).filter(item => item.quantity > 0)
    }));

    // If quantity is 0, remove the item
    if (quantity <= 0) {
      await get().removeItem(itemId);
      return;
    }

    // Sync with backend
    try {
      const response = await apiRequest('PATCH', `/api/cart/items/${itemId}`, { quantity });
      // Apply server-normalized values (server may snap quantity to valid multiple/min/stock-cap)
      if (response) {
        set((state) => ({
          items: state.items.map(item =>
            item.id === itemId
              ? {
                  ...item,
                  quantity: response.quantity !== undefined ? response.quantity : item.quantity,
                  maxQuantity: response.maxQuantity !== undefined ? response.maxQuantity : item.maxQuantity,
                  minOrderQty: response.minOrderQty ?? item.minOrderQty,
                  orderMultiple: response.orderMultiple ?? item.orderMultiple,
                }
              : item
          )
        }));
      }
    } catch (error: any) {
      console.error('Failed to update cart item quantity:', error);
      
      // Check if this is a session expiry error (401)
      const errorMessage = error.message || '';
      if (errorMessage.includes('401') || errorMessage.toLowerCase().includes('unauthorized') || errorMessage.toLowerCase().includes('session')) {
        handleSessionExpiredForCart('update your cart');
        return;
      }
      
      // Show specific error message if it's an inventory error
      if (error.available !== undefined) {
        toast({
          title: "Insufficient Inventory",
          description: error.message || `Only ${error.available} unit(s) available.`,
          variant: "destructive",
        });
      }
      
      // Revert on error
      set({ items: previousItems });
    }
  },

  updatePoNumber: async (poNumber: string) => {
    if (get().isGuestMode()) {
      // Guest mode: Save to localStorage
      set({ poNumber });
      saveGuestPoNumber(poNumber);
      return;
    }

    // Authenticated mode: Optimistic update
    const previousPoNumber = get().poNumber;
    set({ poNumber });

    // Sync with backend
    try {
      await apiRequest('PATCH', '/api/cart', { poNumber });
    } catch (error: any) {
      console.error('Failed to update PO number:', error);
      
      // Check if this is a session expiry error (401)
      const errorMessage = error.message || '';
      if (errorMessage.includes('401') || errorMessage.toLowerCase().includes('unauthorized') || errorMessage.toLowerCase().includes('session')) {
        handleSessionExpiredForCart('save your order details');
        return;
      }
      
      // Revert on error
      set({ poNumber: previousPoNumber });
      toast({
        title: "Failed to save PO number",
        description: "Please try again",
        variant: "destructive",
      });
    }
  },

  clearCart: async () => {
    if (get().isGuestMode()) {
      // Guest mode: Clear localStorage and reset state
      clearGuestCart();
      set({ items: [], poNumber: null });
      return;
    }

    // Authenticated mode: Optimistic update
    const previousItems = get().items;
    set({ items: [] });

    // Sync with backend
    try {
      await apiRequest('DELETE', '/api/cart', {});
    } catch (error: any) {
      console.error('Failed to clear cart:', error);
      
      // Check if this is a session expiry error (401)
      const errorMessage = error.message || '';
      if (errorMessage.includes('401') || errorMessage.toLowerCase().includes('unauthorized') || errorMessage.toLowerCase().includes('session')) {
        handleSessionExpiredForCart('manage your cart');
        return;
      }
      
      // Revert on error
      set({ items: previousItems });
      toast({
        title: "Failed to clear cart",
        description: "Please try again",
        variant: "destructive",
      });
    }
  },

  getTotalAmount: () => {
    // Return total as decimal number
    const items = get().items;
    const totals = items.map(item => multiplyMoney(item.price, item.quantity));
    return sumMoney(totals);
  },

  getItemCount: () => {
    return get().items.reduce((count, item) => count + item.quantity, 0);
  },

  calculateCapacity: (truckSize: string) => {
    const items = get().items;
    
    // Calculate total area used
    let totalAreaUsed = 0;
    for (const item of items) {
      const itemArea = item.dimensions.width * item.dimensions.depth;
      totalAreaUsed += itemArea * item.quantity;
    }

    // Get truck dimensions
    const truckSizes: Record<string, { totalAreaSqInches: number }> = {
      '24ft_box': { totalAreaSqInches: 27648 },
      '53ft_semi': { totalAreaSqInches: 64872 },
      '40ft_container': { totalAreaSqInches: 43424 },
      'unknown': { totalAreaSqInches: Infinity },
    };

    const truck = truckSizes[truckSize] || truckSizes.unknown;
    const capacityUsedPercentage = truck.totalAreaSqInches === Infinity 
      ? 0 
      : (totalAreaUsed / truck.totalAreaSqInches) * 100;
    
    const remainingArea = truck.totalAreaSqInches === Infinity 
      ? Infinity 
      : truck.totalAreaSqInches - totalAreaUsed;
    
    const canFit = remainingArea >= 0;
    
    let warningLevel: 'low' | 'medium' | 'high' = 'low';
    if (capacityUsedPercentage >= 90) {
      warningLevel = 'high';
    } else if (capacityUsedPercentage >= 70) {
      warningLevel = 'medium';
    }

    return {
      totalAreaUsed,
      capacityUsedPercentage: Math.min(capacityUsedPercentage, 100),
      remainingArea: remainingArea === Infinity ? Infinity : Math.max(remainingArea, 0),
      canFit,
      warningLevel,
    };
  },

  setHighlightedModel: (modelId: string | null) => {
    set({ highlightedModelId: modelId });
  },

  getWarehouseCodes: () => {
    const items = get().items;
    const codes = new Set<string>();
    items.forEach(item => {
      if (item.warehouseCode) {
        codes.add(item.warehouseCode);
      }
    });
    return Array.from(codes);
  },

  migrateGuestCart: async () => {
    // Check if guest cart or PO exists in localStorage
    const guestItems = loadGuestCart();
    const guestPo = loadGuestPoNumber();
    
    // If no guest cart items AND no guest PO, nothing to migrate
    if (guestItems.length === 0 && !guestPo) {
      return;
    }

    // Capture how many items were already in the authenticated cart before we add anything
    const existingItemCount = get().items.length;

    console.log('[Cart] Migrating guest cart:', guestItems.length, 'items', guestPo ? `with PO: ${guestPo}` : '', existingItemCount ? `(${existingItemCount} existing server items)` : '');
    
    const failures: Array<{ item: CartItem; error: string }> = [];

    // Sequentially iterate through guest items
    for (const guestItem of guestItems) {
      try {
        await apiRequest('POST', '/api/cart/items', {
          modelId: guestItem.modelId,
          inventoryId: guestItem.inventoryId,
          unitId: guestItem.unitId,
          grade: guestItem.grade,
          quantity: guestItem.quantity,
          price: formatPriceForDb(guestItem.price),
        });
      } catch (error: any) {
        // Use warn instead of error - this is a handled case (item may be sold out, deleted, etc.)
        // The user will see a toast message about partial sync
        console.warn('[Cart] Failed to migrate item (may be unavailable):', guestItem.modelName, guestItem.grade, error?.message || 'Unknown error');
        failures.push({
          item: guestItem,
          error: error.message || 'Unknown error',
        });
      }
    }

    // Migrate guest PO number if it exists
    if (guestPo) {
      try {
        await apiRequest('PATCH', '/api/cart', { poNumber: guestPo });
        console.log('[Cart] Migrated guest PO number:', guestPo);
      } catch (error) {
        console.error('[Cart] Failed to migrate PO number:', error);
        // Continue with migration - PO migration failure shouldn't block cart migration
      }
    }

    const successCount = guestItems.length - failures.length;

    if (failures.length === 0) {
      // All items succeeded - clear guest cart from localStorage
      clearGuestCart();
      console.log('[Cart] Guest cart migration completed successfully');
      const parts: string[] = [];
      if (successCount > 0) parts.push(`${successCount} item${successCount !== 1 ? 's' : ''} added from your session.`);
      if (existingItemCount > 0) parts.push(`Your ${existingItemCount} previously saved item${existingItemCount !== 1 ? 's' : ''} ${existingItemCount !== 1 ? 'are' : 'is'} still here too.`);
      toast({
        title: "Cart ready",
        description: parts.join(' ') || 'Your cart is up to date.',
      });
    } else {
      // Some items failed - keep failed items in localStorage
      const failedItems = failures.map(f => f.item);
      saveGuestCart(failedItems);
      
      const parts: string[] = [];
      if (successCount > 0) parts.push(`${successCount} item${successCount !== 1 ? 's' : ''} added.`);
      parts.push(`${failures.length} item${failures.length !== 1 ? 's' : ''} could not be added (inventory conflict).`);
      if (existingItemCount > 0) parts.push(`Your ${existingItemCount} previously saved item${existingItemCount !== 1 ? 's' : ''} ${existingItemCount !== 1 ? 'are' : 'is'} still here.`);
      
      toast({
        title: "Partial cart sync",
        description: parts.join(' '),
        variant: "destructive",
      });
      
      console.log('[Cart] Guest cart migration completed with failures:', failures);
    }

    // Re-fetch the server cart and update Zustand state so all merged items appear immediately.
    // queryClient.invalidateQueries alone doesn't update the Zustand store because cart items
    // are served from Zustand (not directly from a useQuery hook) in the checkout/cart components.
    try {
      const refreshRes = await fetch('/api/cart', { credentials: 'include' });
      if (refreshRes.ok) {
        const refreshedCart = await refreshRes.json();
        const mergedItems = transformServerCartItems(refreshedCart.items);
        set({ items: mergedItems, poNumber: refreshedCart.poNumber || null });
        console.log('[Cart] Zustand state refreshed after migration:', mergedItems.length, 'items total');
      }
    } catch (err) {
      console.warn('[Cart] Failed to refresh cart state after migration:', err);
    }
  },
}));
